const SHEET_ID=import.meta.env.VITE_SPREADSHEET_ID;
const TOKEN_URL=import.meta.env.VITE_GRETA_TOKEN_URL;
const CHAT_ID=import.meta.env.VITE_CHAT_ID;

let cached={token: null,exp: 0};
let sheetMeta=null;

const sleep=(ms)=> new Promise((resolve)=> setTimeout(resolve,ms));

export async function getAccessToken() {
  if (cached.token && Date.now() < cached.exp) return cached.token;

  const response=await fetch(TOKEN_URL,{
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({chatId: CHAT_ID})
  });
  const data=await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Unable to access site content');
  }

  cached={
    token: data.access_token,
    exp: Date.now() + Math.max(0,data.expires_in - 60) * 1000
  };
  return cached.token;
}

export async function sheetsRequest(path,init={},attempt=0) {
  const token=await getAccessToken();
  const response=await fetch(
    `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}${path}`,
    {
      ...init,
      headers: {
        ...(init.headers || {}),
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  if (response.status===429 && attempt < 4) {
    await sleep(500 * (2 ** attempt) + Math.random() * 250);
    return sheetsRequest(path,init,attempt + 1);
  }

  if (!response.ok) {
    throw new Error(
      `Sheets API ${response.status}: ${await response.text()}`
    );
  }

  if (response.status===204) return {};
  return response.json();
}

export const getRows=(range)=> sheetsRequest(
  `/values/${encodeURIComponent(range)}`
).then((result)=> result.values || []);

export const appendRow=(range,values)=> sheetsRequest(
  `/values/${encodeURIComponent(range)}:append?valueInputOption=USER_ENTERED&insertDataOption=INSERT_ROWS`,
  {
    method: 'POST',
    body: JSON.stringify({values: [values]})
  }
);

export const updateRow=(range,values)=> sheetsRequest(
  `/values/${encodeURIComponent(range)}?valueInputOption=USER_ENTERED`,
  {
    method: 'PUT',
    body: JSON.stringify({values: [values]})
  }
);

export async function getSheetMeta(force=false) {
  if (!force && sheetMeta) return sheetMeta;

  const data=await sheetsRequest('/?fields=sheets.properties');
  sheetMeta=Object.fromEntries(
    (data.sheets || []).map((sheet)=> [
      sheet.properties.title,
      sheet.properties.sheetId
    ])
  );
  return sheetMeta;
}

export async function getSheetIdByTitle(title) {
  let metadata=await getSheetMeta();
  if (metadata[title] !==undefined) return metadata[title];
  metadata=await getSheetMeta(true);
  return metadata[title];
}

function colLetter(number) {
  let output='';
  let value=number;

  while (value > 0) {
    const remainder=(value - 1) % 26;
    output=String.fromCharCode(65 + remainder) + output;
    value=Math.floor((value - 1) / 26);
  }

  return output;
}

export async function ensureTab(title,headers) {
  const metadata=await getSheetMeta();

  if (metadata[title]===undefined) {
    await sheetsRequest('/:batchUpdate',{
      method: 'POST',
      body: JSON.stringify({
        requests: [{addSheet: {properties: {title}}}]
      })
    });
    sheetMeta=null;
  }

  const lastColumn=colLetter(headers.length);
  const headerRows=await getRows(`${title}!A1:${lastColumn}1`);
  const currentHeaders=headerRows[0] || [];
  const needsHeaderUpdate=headers.some(
    (header,index)=> currentHeaders[index] !==header
  );

  if (!currentHeaders.length || needsHeaderUpdate) {
    await updateRow(
      `${title}!A1:${lastColumn}1`,
      headers
    );
  }
}

export async function findRowIndexById(title,id) {
  const rows=await getRows(`${title}!A:A`);

  for (let index=1;index < rows.length;index +=1) {
    if (rows[index]?.[0]===id) return index + 1;
  }

  return -1;
}

export async function deleteRow(title,id) {
  const sheetId=await getSheetIdByTitle(title);
  if (sheetId===undefined) {
    throw new Error(`Tab not found: ${title}`);
  }

  const sheetRow=await findRowIndexById(title,id);
  if (sheetRow < 0) {
    throw new Error(`Record not found: ${id}`);
  }

  const startIndex=sheetRow - 1;
  const result=await sheetsRequest('/:batchUpdate',{
    method: 'POST',
    body: JSON.stringify({
      requests: [{
        deleteDimension: {
          range: {
            sheetId,
            dimension: 'ROWS',
            startIndex,
            endIndex: startIndex + 1
          }
        }
      }]
    })
  });

  sheetMeta=null;
  return result;
}